import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, extractApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useT } from "@/lib/LanguageContext";
import { CreditCard, ExternalLink, Loader2, CheckCircle2, Clock, AlertCircle, XCircle } from "lucide-react";

/**
 * StripeConnectCard — the practitioner's Stripe account connection (Express onboarding).
 *
 * WHAT THIS CARD'S FEE SENTENCE IS ABOUT, and it is not everything. `status.fee` comes
 * from `modules.payments.service._fee_summary`, which reads the LEGACY `platform_fees`
 * collection — the commission on course and appointment CHECKOUTS. The Payment Gateway
 * settles per-minute teleconsultations and marketplace sales against a different
 * authority (`commission_rules`), and the two are separate on purpose during the cutover
 * (see `platform_helpers.compute_checkout_breakdown`).
 *
 * Until 2026-08-28 this card said "a platform fee of {fee} per transaction" — an
 * unscoped claim about every sale, rendered as "0%" on a stack where the gateway was
 * charging 20%. It now names the sales it governs and points at the wallet lower down
 * the same page (`PaymentsAdmin`), which states the gateway's own rate.
 */
export const StripeConnectCard = () => {
  const t = useT("admin");
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [searchParams] = useSearchParams();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/tenant/stripe-connect/status");
      setStatus(data);
    } catch (e) {
      setStatus({ error: extractApiError(e, t("sconn.error")) });
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const connect = searchParams.get("connect");
    if (connect === "return") {
      toast.success(t("sconn.returnToast"));
      load();
    } else if (connect === "refresh") {
      // Stripe returns here when the AccountLink (single-use, expiring) dies mid-
      // onboarding — with no message the practitioner was stuck with no explanation.
      toast.info(t("sconn.refreshToast"));
      load();
    }
  }, [searchParams, load, t]);

  // `mode` decides the tab, because the two links are different KINDS of trip.
  // "onboarding" is an AccountLink that comes BACK here (the backend builds its
  // return_url as `{origin}/admin/payments?connect=return`), so it belongs in this tab.
  // "manage" is a login link into Stripe's Express dashboard, which has no way back —
  // replacing this tab with it simply destroyed the dashboard the practitioner was on.
  // The backend returns "manage" exactly when the account exists and charges are on
  // (service.connect_onboard), which is what `wantsNewTab` recomputes below.
  //
  // The blank tab is opened SYNCHRONOUSLY inside the click: a `window.open` after an
  // `await` is a popup, and popup blockers eat it. `null` means the blocker won anyway
  // — we then navigate in place rather than leave a dead button.
  const onboard = async () => {
    const wantsNewTab = !!status?.connected && !!status?.charges_enabled;
    const tab = wantsNewTab ? window.open("", "_blank") : null;
    setBusy(true);
    try {
      const { data } = await api.post("/tenant/stripe-connect/onboard", {
        return_url: window.location.origin,
      });
      if (tab && data.mode === "manage") {
        tab.location.href = data.url;
        setBusy(false);
      } else {
        // Stale `charges_enabled` — the backend fell back to onboarding (a flow that
        // returns here). Close the side tab instead of stranding a blank page.
        if (tab) tab.close();
        window.location.href = data.url;
      }
    } catch (e) {
      if (tab) tab.close();
      toast.error(extractApiError(e, t("sconn.unavailable")));
      setBusy(false);
    }
  };

  if (!status) {
    return (
      <div className="bg-white rounded-2xl border border-eh-border-soft p-6 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-eh-muted" />
      </div>
    );
  }

  if (status.error) {
    // A failed status fetch (401/403/500) used to render the card as "Not connected"
    // with an active Connect button — visually identical to a brand-new account.
    return (
      <div data-testid="connect-status-error" className="bg-white rounded-2xl border border-rose-200 p-6">
        <div className="flex items-center gap-2 mb-2">
          <AlertCircle className="w-5 h-5 text-rose-500" />
          <h3 className="font-display text-xl">Stripe Connect</h3>
        </div>
        <p className="text-sm text-rose-700 mb-4">{t("sconn.statusError")} {String(status.error)}</p>
        <Button size="sm" variant="outline" onClick={load} data-testid="connect-retry-btn">
          {t("sconn.retry")}
        </Button>
      </div>
    );
  }

  // QA2 H2-03 — `ready` used to be `charges_enabled` alone, and `payouts_enabled` was
  // fetched only to pick an em-dash on the detail row. An account with charges ON and
  // payouts OFF therefore rendered the SAME green "Active" badge as a healthy one: the
  // practitioner sells for weeks while every dollar piles up in a Stripe balance that
  // cannot reach their bank. Getting paid takes BOTH legs, so readiness asks for both.
  const connected = !!status.connected;
  const chargesOn = !!status.charges_enabled;
  const payoutsOn = !!status.payouts_enabled;
  // THE SERVER'S VERDICT, not a second copy of the rule. `payouts.validate_payout_
  // eligibility` refuses a destination on four conditions; this card knew two of them
  // and was never sent the others, so an account with `transfers: pending` — which
  // Stripe issues with payouts still enabled — wore the emerald "Active" badge while
  // every withdrawal came back PAYOUT_DESTINATION_NOT_READY. `payouts_ready` is that
  // gate's own predicate (`modules/payments/connect_readiness.py`), reported.
  //
  // `undefined` is NOT false: a payload without the field is a server that does not
  // compute readiness, and treating silence as a blockage would paint every healthy
  // account as held. Absent falls back to the two booleans — precisely the previous
  // behaviour — while an explicit `false` blocks.
  const payoutsBlocked = status.payouts_ready === false;
  const blockedReason = payoutsBlocked ? status.payouts_blocked_reason : null;
  const ready = connected && chargesOn && payoutsOn && !payoutsBlocked;
  const payoutsHeld = connected && chargesOn && (!payoutsOn || payoutsBlocked);
  const pending = connected && !chargesOn;

  // WHAT Stripe is waiting for. The payouts-held block can only say "finish the
  // verification Stripe is asking for"; these are the items themselves, stored on
  // `tenants.stripe_connect.requirements` and reported by the status endpoint since
  // 2026-08-28. Rendered from the payload alone — a build talking to a backend that does
  // not send them shows nothing, never an all-clear panel for a check that did not run.
  const requirements = status.requirements;
  const pastDue = requirements?.past_due || [];
  const currentlyDue = requirements?.currently_due || [];
  const disabledReason = requirements?.disabled_reason || null;
  const hasRequirements = pastDue.length > 0 || currentlyDue.length > 0 || !!disabledReason;

  return (
    <div data-testid="stripe-connect-card" className="bg-white rounded-2xl border border-eh-border-soft overflow-hidden">
      <div className="h-2 bg-gradient-to-r from-[#635BFF] to-[#0A2540]" />
      <div className="p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-xl flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[#635BFF]" /> Stripe Connect
          </h3>
          {ready ? (
            <span className="text-xs bg-emerald-100 text-emerald-800 px-2.5 py-0.5 rounded-full inline-flex items-center gap-1" data-testid="connect-status-ready">
              <CheckCircle2 className="w-3 h-3" /> {t("sconn.active")}
            </span>
          ) : payoutsHeld ? (
            <span className="text-xs bg-rose-100 text-rose-800 px-2.5 py-0.5 rounded-full inline-flex items-center gap-1" data-testid="connect-status-payouts-held">
              <AlertCircle className="w-3 h-3" /> {t("sconn.payoutsHeld", null, "Payouts on hold")}
            </span>
          ) : pending ? (
            <span className="text-xs bg-amber-100 text-amber-800 px-2.5 py-0.5 rounded-full inline-flex items-center gap-1" data-testid="connect-status-pending">
              <Clock className="w-3 h-3" /> {t("sconn.toFinalize")}
            </span>
          ) : (
            <span className="text-xs bg-eh-cream text-eh-muted px-2.5 py-0.5 rounded-full" data-testid="connect-status-none">{t("sconn.notConnected")}</span>
          )}
        </div>

        {/* `fee` comes from the backend ONLY — no hardcoded fallback. That constant is
            precisely the one this project removed, and showing it as a fallback
            advertised a rate no checkout charges. Empty = the service types do not all
            charge the same, so they are listed rather than summarised into a false one. */}
        <p className="text-sm text-eh-muted mb-2">
          {status.fee
            ? t("sconn.desc", { fee: status.fee })
            : t("sconn.descVaries")}
        </p>
        {/* The OTHER commission. Always rendered, whether or not the legacy summary
            collapsed to one label: the gateway settles a different set of sales at a
            different rate either way, and a fee sentence that mentions only one of the
            two is the defect this note closes. */}
        <p className="text-xs text-eh-muted mb-4" data-testid="connect-fee-scope">
          {t("sconn.feeScopeNote")}
        </p>
        {!status.fee && status.fees && (
          <ul className="text-xs text-eh-muted mb-4 space-y-0.5" data-testid="connect-fees-per-type">
            {Object.entries(status.fees).map(([kind, label]) => (
              <li key={kind}>
                {t(`sconn.svc.${kind}`, {}, kind)} — <b>{label}</b>
              </li>
            ))}
          </ul>
        )}

        {status.connected && (
          <div className="text-xs text-eh-muted mb-4 space-y-1">
            <div>{t("sconn.account")} <code className="bg-eh-cream/40 px-1.5 py-0.5 rounded">{status.account_id}</code></div>
            {/* H2-03's decisive measurement: "Payments ✓" and "Payouts —" rendered in
                IDENTICAL computed style — same colour, same weight, same size, and an
                em-dash for "you will never be paid". A leg that is OFF is the most
                expensive fact on this card; it now says so in words, in rose, in bold. */}
            <div className="flex gap-3">
              <span data-testid="connect-leg-charges" className={chargesOn ? "" : "text-rose-700 font-medium inline-flex items-center gap-1"}>
                {!chargesOn && <XCircle className="w-3 h-3" />}
                {t("sconn.payments")} {chargesOn ? "✓" : t("sconn.legOff", null, "disabled")}
              </span>
              <span data-testid="connect-leg-payouts" className={payoutsOn ? "" : "text-rose-700 font-medium inline-flex items-center gap-1"}>
                {!payoutsOn && <XCircle className="w-3 h-3" />}
                {t("sconn.payouts")} {payoutsOn ? "✓" : t("sconn.legOff", null, "disabled")}
              </span>
            </div>
          </div>
        )}

        {/* Charges on, payouts off: the card USED to render zero warnings in this state,
            under copy promising "the funds go to your connected Stripe account". This
            block is the unmissable half; `connect-status-payouts-held` is the glanceable
            half. Rose, not amber: unlike `paid_needs_attention`, nothing here is waiting
            on a decision — money is already being taken that cannot be paid out. */}
        {payoutsHeld && (
          <div className="bg-rose-50 border border-rose-300 rounded-lg p-3 text-xs text-rose-900 mb-4 flex gap-2" data-testid="connect-payouts-held-note">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              <b>{t("sconn.payoutsHeldTitle", null, "You are collecting money you cannot withdraw.")}</b>{" "}
              {t("sconn.payoutsHeldBody", null, "Stripe accepts your clients' payments but is holding your payouts, so nothing reaches your bank account. Open your Stripe account and finish the verification Stripe is asking for.")}
              {/* WHICH blocker, when the server named one. "Finish the verification"
                  with no object sends a practitioner hunting through the Express
                  dashboard for something that may not even be a verification — a
                  transfers capability still pending is a different errand. */}
              {blockedReason && (
                <span className="block mt-1 font-medium" data-testid="connect-payouts-blocked-reason">
                  {t(`sconn.blocked.${blockedReason}`, null, blockedReason)}
                </span>
              )}
            </span>
          </div>
        )}

        {hasRequirements && (
          <div className="rounded-lg border border-eh-border-soft p-3 text-xs mb-4 space-y-2" data-testid="connect-requirements">
            <b className="text-eh-ink">{t("sconn.reqTitle", null, "Stripe still needs:")}</b>
            {/* PAST DUE IS THE EMERGENCY — these are the items already blocking the
                account, which is why they are rose and listed first. `currently_due`
                will block soon; rendering the two identically would flatten a deadline
                that has passed into one that has not. */}
            {pastDue.length > 0 && (
              <ul className="text-rose-800 font-medium list-disc pl-4 space-y-0.5" data-testid="connect-requirements-past-due">
                {pastDue.map((item) => (
                  <li key={item}>{t(`sconn.req.${item}`, null, item)}</li>
                ))}
              </ul>
            )}
            {currentlyDue.length > 0 && (
              <ul className="text-amber-800 list-disc pl-4 space-y-0.5" data-testid="connect-requirements-currently-due">
                {currentlyDue.map((item) => (
                  <li key={item}>{t(`sconn.req.${item}`, null, item)}</li>
                ))}
              </ul>
            )}
            {/* Stripe's own machine reason, verbatim. It is not localised copy and is
                not translated here: it is the string a practitioner quotes to Stripe
                support, and a translated one would not be findable. */}
            {disabledReason && (
              <p className="text-eh-muted" data-testid="connect-requirements-reason">
                {t("sconn.reqDisabledReason", null, "Stripe's reason:")}{" "}
                <code className="bg-eh-cream/40 px-1.5 py-0.5 rounded">{disabledReason}</code>
              </p>
            )}
          </div>
        )}

        {status.demo_mode && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 mb-4 flex gap-2" data-testid="connect-demo-note">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            {t("sconn.demoNote")}
          </div>
        )}

        {status.blocked && (
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-800 mb-4 flex gap-2" data-testid="connect-blocked-note">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            {t("sconn.blockedNote")}
          </div>
        )}

        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={onboard}
            disabled={busy || status.demo_mode || status.blocked}
            data-testid="connect-stripe-btn"
            className={payoutsHeld ? "bg-rose-700 text-white hover:bg-rose-800" : "bg-[#635BFF] text-white hover:bg-[#5249e0]"}
          >
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ExternalLink className="w-3 h-3 mr-1" />}
            {ready
              ? t("sconn.manage")
              : payoutsHeld
                ? t("sconn.finishVerification", null, "Finish verification to get paid")
                : pending ? t("sconn.finalize") : t("sconn.connect")}
          </Button>
          <a href="https://stripe.com/connect" target="_blank" rel="noopener noreferrer" className="text-xs text-eh-muted hover:text-eh-ink inline-flex items-center self-center">
            {t("sconn.docs")} <ExternalLink className="w-3 h-3 ml-0.5" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default StripeConnectCard;
